/**
 * Session Token Storage Utility (AL-213)
 *
 * Stores auth tokens in sessionStorage instead of cookies.
 *
 * Why sessionStorage over cookies for a shared-machine security posture:
 *  - Cookies persist across browser tabs and survive tab close until they
 *    expire or are explicitly deleted — tokens can be recovered from a
 *    closed tab on a shared/unattended machine.
 *  - sessionStorage is scoped to a single tab and is automatically wiped
 *    when that tab closes, even without an explicit logout, shortening the
 *    window for token theft/XSS exfiltration.
 *
 * Trade-off: sessionStorage is per-tab, so opening a second tab will not
 * automatically be authenticated — the user needs to log in again in that
 * tab. This matches the pattern already used by the C2 verify app's
 * cognitoC2Service.ts, where single-tab sessions have not been a problem.
 */

const PREFIX = 'c1_';

/**
 * Store a token value in sessionStorage under a prefixed key.
 */
export function setToken(name: string, value: string): void {
	try {
		sessionStorage.setItem(`${PREFIX}${name}`, value);
	} catch (err) {
		// sessionStorage can throw in rare cases (e.g. quota exceeded, private
		// browsing restrictions on some browsers) — fail silently, the caller
		// treats a missing token as "not authenticated".
	}
}

/**
 * Retrieve a token value from sessionStorage.
 */
export function getToken(name: string): string | null {
	try {
		return sessionStorage.getItem(`${PREFIX}${name}`);
	} catch (err) {
		return null;
	}
}

/**
 * Delete a token value from sessionStorage.
 */
export function deleteToken(name: string): void {
	try {
		sessionStorage.removeItem(`${PREFIX}${name}`);
	} catch (err) {
		// ignore
	}
}

/**
 * Clear all known session tokens.
 */
export function clearAllTokens(): void {
	deleteToken('idToken');
	deleteToken('jwtToken');
	deleteToken('accessToken');
	deleteToken('refreshToken');
}
